/**
 * @fileoverview Priority Outreach API Module
 * This module provides API functions for managing priority outreach data including
 * fetching outreach records, searching for members/providers/pharmacies/programs,
 * and managing member information.
 */

import api from "./api";
import { capitalizeFirstLetter } from "../utils/capitalize";
import { isUserL9Role } from "../utils/role";

const hasFilterPayload = (body) =>
  !!(
    (body &&
      (body.programIds?.length ||
        body.carrierCodes?.length ||
        body.groupNames?.length ||
        body.assigneeIds?.length ||
        body.outcomes?.length ||
        body.recentActivityOutcomes?.length ||
        body.recentActivityReasons?.length)) ||
    body.memberStatuses?.length
  );

const isSearchTerm = (search) => !!search?.trim();

const buildQueryParams = (qp, searchActive, filtersActive) => {
  const base = {
    outreachType: qp.outreachType,
    ...(qp.memberBadge && { memberBadge: qp.memberBadge }),
  };

  const filtered = Object.fromEntries(
    Object.entries(qp).filter(([key]) => key !== "page" && key !== "size"),
  );

  const pagination = {
    page: Number.isInteger(qp.page) ? qp.page : 0,
    size: Number.isInteger(qp.size) ? qp.size : 200,
  };
  return { ...base, ...filtered, ...pagination };
};

const buildRequestBody = (body, searchActive) => {
  let outcomes = [];
  if (Array.isArray(body.outcomes)) {
    outcomes = body.outcomes;
  } else if (Array.isArray(body.recentActivityOutcomes)) {
    outcomes = body.recentActivityOutcomes.map((outcome) => ({
      outcome,
      subCategory: [],
    }));
  }

  return {
    programIds: Array.isArray(body.programIds) ? body.programIds : [],
    carrierCodes: Array.isArray(body.carrierCodes) ? body.carrierCodes : [],
    groupNumbers: Array.isArray(body.groupNumbers) ? body.groupNumbers : [],
    assigneeIds: Array.isArray(body.assigneeIds) ? body.assigneeIds : [],
    memberStatuses: Array.isArray(body.memberStatuses)
      ? body.memberStatuses
      : [],
    outcomes,
  };
};

const isPersonCode00 = (code) => {
  if (code == null) return false;
  const str = String(code).trim();
  const num = Number.parseInt(str, 10);
  return str === "00" || str === "0" || num === 0;
};

const isPersonCode01 = (code) => {
  if (code == null) return false;
  const str = String(code).trim();
  const num = Number.parseInt(str, 10);
  return str === "01" || str === "1" || num === 1;
};

const isPrimaryPersonCode = (code) =>
  isPersonCode00(code) || isPersonCode01(code);

// The Member Outreach list shows one row per subscriber (primary member),
// with the rest of their family nested under it. Who's primary comes from
// relCode: "1"/"01" is the primary, any other relCode is family. If relCode
// is null/missing/0, fallback to personCode: "00" or "01" is also the primary
// family member, any other personCode is family.
const relCodeKind = (item) => {
  const rawRel = item.member?.relCode ?? item?.relCode;
  const relStr = String(rawRel ?? "").trim();
  const isRelCodeNullOrZero =
    rawRel == null ||
    relStr === "" ||
    relStr === "null" ||
    relStr === "undefined" ||
    Number(rawRel) === 0;

  if (isRelCodeNullOrZero) {
    const personCode = item.member?.personCode ?? item?.personCode;
    if (isPrimaryPersonCode(personCode)) {
      return "primary";
    }
    const pcStr = String(personCode ?? "").trim();
    if (!pcStr || pcStr === "null" || pcStr === "undefined") {
      return "unknown";
    }
    return "family";
  }
  return Number(rawRel) === 1 ? "primary" : "family";
};

// Returns the set of items that are a primary member. Items with no
// subscriberNum can't be grouped with anyone, so each counts as its own primary.
// For a subscriber group, primary members are determined by relCode === 1, or
// when relCode is null/missing/0, personCode "00" or "01".
const findPrimaryItems = (items) => {
  const primaries = new Set();
  const bySubscriber = new Map();
  items.forEach((item) => {
    const subscriberNum = item.member?.subscriberNum ?? item?.subscriberNum;
    if (!subscriberNum) {
      primaries.add(item);
      return;
    }
    const list = bySubscriber.get(subscriberNum) || [];
    list.push(item);
    bySubscriber.set(subscriberNum, list);
  });
  bySubscriber.forEach((group) => {
    // 1. Explicit primary by relCode === 1 ("1" or "01")
    const explicitPrimaries = group.filter((item) => {
      const rawRel = item.member?.relCode ?? item?.relCode;
      const str = String(rawRel ?? "").trim();
      return (
        rawRel != null &&
        str !== "" &&
        str !== "null" &&
        str !== "undefined" &&
        Number(rawRel) === 1
      );
    });
    if (explicitPrimaries.length > 0) {
      explicitPrimaries.forEach((item) => primaries.add(item));
      return;
    }

    // 2. If relCode is null/missing/0, fallback to personCode:
    // Both "00" and "01" are primary family members
    const nullRelPrimaries = group.filter((item) => {
      const rawRel = item.member?.relCode ?? item?.relCode;
      const str = String(rawRel ?? "").trim();
      const isNullRel =
        rawRel == null ||
        str === "" ||
        str === "null" ||
        str === "undefined" ||
        Number(rawRel) === 0;
      if (!isNullRel) return false;
      return isPrimaryPersonCode(item.member?.personCode ?? item?.personCode);
    });
    if (nullRelPrimaries.length > 0) {
      nullRelPrimaries.forEach((item) => primaries.add(item));
      return;
    }

    // 3. Fallback to any item whose relCodeKind evaluates to "primary"
    const byKind = group.filter((item) => relCodeKind(item) === "primary");
    if (byKind.length > 0) {
      byKind.forEach((item) => primaries.add(item));
    }
  });
  return primaries;
};

// A dependent (a non-primary family member) only shows up in the outreach
// feed at all if they have their own outreach item - which means they're
// already a HALO member. So the Family column doesn't need a separate
// lookup: every item in a page shares its subscriberNum with its family, and
// any of those already carry a member id + name. Build that map once per
// page from the raw (unfiltered) content, before the person-code filter
// drops the dependent rows from the list itself.
const buildFamilyBySubscriber = (content) => {
  const bySubscriber = new Map();
  content.forEach((item) => {
    const subscriberNum = item.member?.subscriberNum;
    const memberId = item.member?.id != null ? String(item.member.id) : "";
    if (!subscriberNum || !memberId) return;
    const list = bySubscriber.get(subscriberNum) || [];
    if (!list.some((m) => m.memberId === memberId)) {
      list.push({
        memberId,
        name: `${capitalizeFirstLetter(item.member?.fullName || "")}`.trim(),
        assignedPcaId: item.member?.assignedPcaId ?? null,
        assignedPca: item.member?.assignedPca || "",
      });
    }
    bySubscriber.set(subscriberNum, list);
  });
  return bySubscriber;
};

// The outreach list's member-level activity fields (outcomeDisplayName,
// recentActivityAt, colourCode, ...) are only populated when the backend can
// pick a single "current" program for that member - with more than one
// program, it can come back entirely empty even though one of the member's
// programs has real, non-null activity (see memberPrograms[].recentActivityDto).
// Falling back to the most recent program with actual activity keeps the
// Recent Activity column from showing N/A in that case.
const findLatestProgramActivity = (memberPrograms) => {
  const withActivity = (memberPrograms || [])
    .map((p) => p.recentActivityDto)
    .filter((dto) => dto?.outcomeDisplayName || dto?.outcome);
  if (withActivity.length === 0) return null;
  return [...withActivity].sort(
    (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0),
  )[0];
};

const getCgStatus = (cg) => {
  if (!cg) return "N/A";
  return (
    cg.outcomeDisplayName ||
    (cg.outcome ? capitalizeFirstLetter(cg.outcome) : "N/A")
  );
};

const getCgReason = (cg) => {
  if (!cg) return "";
  return (
    cg.subReasonDisplayName ||
    (cg.subReason ? capitalizeFirstLetter(cg.subReason) : "")
  );
};

const mapOutreachItem = (item, outreachType, familyBySubscriber) => {
  const selfMemberId = item.member?.id != null ? String(item.member.id) : "";
  const familyMembers = (
    familyBySubscriber?.get(item.member?.subscriberNum) || []
  ).filter((m) => m.memberId !== selfMemberId);

  // A member with no assignee of their own - typically the primary when
  // they have no opportunity of their own (see isNonMember in columns.jsx) -
  // shows the family's PCA assignment instead, so the Assignee dropdown
  // isn't blank just because this particular person has nothing to assign
  // an owner to yet.
  const familyPca = !item.member?.assigneeId
    ? familyMembers.find((m) => m.assignedPcaId != null)
    : null;

  const isL9 = isUserL9Role();
  const cgItem = item.cgRecentActivity;
  const fallbackActivity = isL9
    ? null
    : findLatestProgramActivity(item.memberPrograms);

  const itemStatus = isL9
    ? getCgStatus(cgItem)
    : item.outcomeDisplayName || fallbackActivity?.outcomeDisplayName || "N/A";

  const itemReason = isL9
    ? getCgReason(cgItem)
    : item.subCategoryDisplayName ||
      fallbackActivity?.subCategoryDisplayName ||
      "";

  const itemTimestamp = isL9
    ? cgItem?.timestamp || ""
    : item.recentActivityAt || fallbackActivity?.timestamp || "";

  const itemStatusColor = isL9
    ? cgItem?.colourCode || ""
    : item?.colourCode || fallbackActivity?.colourCode || "";

  return {
    member: {
      memberId: item.member?.id?.toString() || "",
      name: `${capitalizeFirstLetter(item.member?.fullName || "")} `.trim(),
      personCode: item.member?.personCode || "",
      familyMembers,
      language: item.member?.language || "N/A",
      timeZone: item.member?.timezone || "N/A",
      priorityLevel: item.member?.priorityLevel || "",
      gender: "",
      age: item.member?.dob
        ? new Date().getFullYear() - new Date(item.member.dob).getFullYear()
        : "",
      dob: item.member?.dob || "",
      knownAllergies: item.member?.allergy ? [item.member.allergy] : [],
      contact: {
        phone: item.member?.phoneNumber || "",
        email: item.member?.email || "",
      },
      address: `${item.member?.address || ""}, ${item.member?.city || ""}, ${
        item.member?.state || ""
      }`.trim(),
    },
    memberBadge: item.memberBadge || "",
    type: outreachType || "N/A",
    provider: {
      name: item.memberPrograms?.[0]?.provider?.name
        ? capitalizeFirstLetter(item.memberPrograms[0].provider.name)
        : "",
      contact: item.memberPrograms?.[0]?.provider?.contact || "",
    },
    pharmacy: {
      name: item.memberPrograms?.[0]?.pharmacy?.name
        ? capitalizeFirstLetter(item.memberPrograms[0].pharmacy.name)
        : "",
      contact: item.memberPrograms?.[0]?.pharmacy?.contact || "",
    },
    assignee: {
      name: item.member?.assignee || familyPca?.assignedPca || "Unassigned",
      id: item.member?.assigneeId || familyPca?.assignedPcaId || "",
    },
    cag: {
      name: `${capitalizeFirstLetter(item.member?.carrierName)} (${
        item.member?.carrierCode
      })`,
      contact: `${capitalizeFirstLetter(item.member?.groupName)} (${
        item.member?.groupNum
      })`,
    },
    programs:
      item.memberPrograms?.map((p) => {
        const cgProg = p.cgRecentActivity;

        const progStatus = isL9
          ? getCgStatus(cgProg)
          : p.recentActivityDto?.outcomeDisplayName || "N/A";

        const progReason = isL9
          ? getCgReason(cgProg)
          : p.recentActivityDto?.subCategoryDisplayName || "";

        const progTimestamp = isL9
          ? cgProg?.timestamp || ""
          : p.recentActivityDto?.timestamp || "";

        const progStatusColor = isL9
          ? cgProg?.colourCode || ""
          : p.recentActivityDto?.colourCode || "";

        return {
          name: p.program?.programName || "",
          status: p.status || "",
          recentActivity: {
            timestamp: progTimestamp,
            status: progStatus,
            reason: progReason,
            statusColor: progStatusColor,
          },
          cgRecentActivity: cgProg || null,
        };
      }) || [],
    recentActivity: {
      status: itemStatus,
      role: item.recentActivityRole || "",
      timestamp: itemTimestamp,
      programStatus: item.member?.status || "",
      reason: itemReason,
      statusColor: itemStatusColor,
    },
    cgRecentActivity: cgItem || null,
    latestDateAdded: item?.latestDateAdded || "",
    paActions: item?.paActions || [],
    rejectionCodes: item?.rejectionCodes || [],
  };
};

const transformContent = (content, outreachType) => {
  const familyBySubscriber = buildFamilyBySubscriber(content);
  // A family member with no program/opportunity of their own isn't listed at
  // all - only they're hidden; a primary with none still shows (N/A) so the
  // family members that do have opportunities have something to nest under.
  const hasPrograms = (item) =>
    Array.isArray(item.memberPrograms) && item.memberPrograms.length > 0;
  // L9 users get a flat list: no primary/family grouping, and only members
  // that have memberPrograms are listed (primaries without any are hidden too).
  if (isUserL9Role()) {
    return content.filter(hasPrograms).map((item) =>
      mapOutreachItem(
        {
          ...item,
          name: item.name ? capitalizeFirstLetter(item.name) : item.name,
        },
        outreachType,
        familyBySubscriber,
      ),
    );
  }
  const primaryItems = findPrimaryItems(content);
  const normalized = content
    .filter((item) => primaryItems.has(item) || hasPrograms(item))
    .map((item) => ({
      ...item,
      name: item.name ? capitalizeFirstLetter(item.name) : item.name,
      isPrimaryMember: primaryItems.has(item),
    }));

  // Family members (everyone but the primary) are nested under their primary
  // subscriber's row as collapsible sub-rows. A dependent whose primary isn't
  // in this page of results has nothing to nest under, so it stays a
  // top-level row of its own.
  const primaryBySubscriber = new Map();
  normalized.forEach((item) => {
    const subscriberNum = item.member?.subscriberNum;
    if (
      subscriberNum &&
      item.isPrimaryMember &&
      !primaryBySubscriber.has(subscriberNum)
    ) {
      primaryBySubscriber.set(subscriberNum, item);
    }
  });

  const dependentsBySubscriber = new Map();
  const topLevel = [];
  normalized.forEach((item) => {
    const subscriberNum = item.member?.subscriberNum;
    if (
      subscriberNum &&
      !item.isPrimaryMember &&
      primaryBySubscriber.has(subscriberNum)
    ) {
      const list = dependentsBySubscriber.get(subscriberNum) || [];
      list.push(item);
      dependentsBySubscriber.set(subscriberNum, list);
    } else {
      topLevel.push(item);
    }
  });

  return topLevel.map((item) => {
    const row = mapOutreachItem(item, outreachType, familyBySubscriber);
    // Only the one primary chosen above owns the sub-rows (guards against a
    // family somehow carrying more than one primary).
    const dependents =
      primaryBySubscriber.get(item.member?.subscriberNum) === item
        ? dependentsBySubscriber.get(item.member?.subscriberNum)
        : null;
    if (dependents?.length) {
      row.subRows = dependents.map((dep) => ({
        ...mapOutreachItem(dep, outreachType, familyBySubscriber),
        isFamilyMember: true,
      }));
    }
    return row;
  });
};

// Subscriber numbers we've already looked up and confirmed have no primary
// (primary) outreach item - the primary has no outreach item of
// their own at all, so no search will ever surface one for them. Cached at
// module scope so repeat page loads/renders of the same search don't keep
// re-issuing the same futile lookup.
const __UNRESOLVABLE_SUBSCRIBER_CACHE = new Set();

// A search whose term matches only a dependent (a non-primary family member) comes
// back with just that person's own item - not their primary subscriber's -
// so the person-code filter above would drop it and the search would show
// nothing. The /outreach search endpoint also accepts a subscriberNum as the
// search value, so for every such dependent we re-run it with their
// subscriberNum to pull in that whole family's outreach items (the primary's
// included, if they have one) instead of a name/text match.
const resolveDependentMatchesToPrimaries = async (
  content,
  requestBody,
  queryParams,
) => {
  // If the search term itself is already this exact subscriberNum, a
  // follow-up search for that same subscriberNum would hit the backend with
  // an identical query and deterministically get the identical (still
  // primary-less) result back - the primary simply has no outreach item of
  // their own to be found by any search. Skip those rather than firing a
  // pointless duplicate request.
  const originalSearchTerm = (queryParams.search || "").trim();

  const primaryItems = findPrimaryItems(content);
  const subscriberNumsNeedingLookup = [
    ...new Set(
      content
        .filter((item) => !primaryItems.has(item))
        .map((item) => item.member?.subscriberNum)
        .filter(Boolean),
    ),
  ].filter(
    (subscriberNum) =>
      subscriberNum !== originalSearchTerm &&
      !__UNRESOLVABLE_SUBSCRIBER_CACHE.has(subscriberNum) &&
      !content.some(
        (item) =>
          item.member?.subscriberNum === subscriberNum &&
          primaryItems.has(item),
      ),
  );

  if (subscriberNumsNeedingLookup.length === 0) return content;

  const lookups = await Promise.all(
    subscriberNumsNeedingLookup.map((subscriberNum) =>
      api
        .post(`/outreach`, buildRequestBody(requestBody, true), {
          // A single-subscriber lookup never needs to paginate past page 0 -
          // whatever page the broader search was browsing is irrelevant (and
          // would return nothing) for this narrower query.
          params: buildQueryParams(
            { ...queryParams, search: subscriberNum, page: 0 },
            true,
            hasFilterPayload(requestBody),
          ),
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        })
        .then((res) => {
          const items = Array.isArray(res.data?.content)
            ? res.data.content
            : [];
          const lookupPrimaries = findPrimaryItems(items);
          const hasPrimary = items.some((item) => lookupPrimaries.has(item));
          if (!hasPrimary) __UNRESOLVABLE_SUBSCRIBER_CACHE.add(subscriberNum);
          return items;
        })
        .catch((e) => {
          console.error("Failed to resolve family for subscriber", e);
          return [];
        }),
    ),
  );

  const resolvedSubscriberNums = new Set(subscriberNumsNeedingLookup);
  const merged = content.filter(
    (item) => !resolvedSubscriberNums.has(item.member?.subscriberNum),
  );
  const seenMemberIds = new Set(merged.map((item) => item.member?.id));
  lookups.flat().forEach((item) => {
    const id = item.member?.id;
    if (!seenMemberIds.has(id)) {
      seenMemberIds.add(id);
      merged.push(item);
    }
  });
  return merged;
};

/**
 * Fetches priority outreach data with optional filtering and pagination
 * @async
 * @function fetchPriorityOutreach
 * @param {Object} params - Parameters for the outreach request
 * @param {Object} [params.body={}] - Request body containing filter criteria
 * @param {string[]} [params.body.memberIds] - Array of member IDs to filter by
 * @param {string[]} [params.body.providerNpis] - Array of provider NPIs to filter by
 * @param {string[]} [params.body.pharmacyNpis] - Array of pharmacy NPIs to filter by
 * @param {string[]} [params.body.programIds] - Array of program IDs to filter by
 * @param {string} [params.outreachType] - Type of outreach (e.g., "PRIORITY_OUTREACH")
 * @param {string} [params.memberBadge] - Member badge filter
 * @param {string} [params.search] - Search term for filtering results
 * @param {number} [params.page=0] - Page number for pagination (0-based)
 * @param {number} [params.size=30] - Number of items per page
 * @returns {Promise<Object>} Promise resolving to an object containing:
 *   - content: Array of transformed outreach records
 *   - totalElements: Total number of records available
 * @throws {Error} Throws error if API request fails
 * @example
 * // Fetch first page with default size
 * const result = await fetchPriorityOutreach({ page: 0, size: 30 });
 *
 * // Fetch with specific member filters
 * const filtered = await fetchPriorityOutreach({
 *   body: { memberIds: ['123', '456'] },
 *   outreachType: 'PRIORITY_OUTREACH'
 * });
 */
export const fetchPriorityOutreach = async (params = {}) => {
  const { body: requestBody = {}, ...queryParams } = params;
  const searchActive = isSearchTerm(queryParams.search);
  const filtersActive = hasFilterPayload(requestBody);
  const finalQueryParams = buildQueryParams(
    queryParams,
    searchActive,
    filtersActive,
  );
  const finalRequestBody = buildRequestBody(requestBody, searchActive);

  const response = await api.post(`/outreach`, finalRequestBody, {
    params: finalQueryParams,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  if (Array.isArray(response.data.content)) {
    const rawContent = response.data.content;
    const content = searchActive
      ? await resolveDependentMatchesToPrimaries(
          rawContent,
          requestBody,
          queryParams,
        )
      : rawContent;
    const mappedContent = transformContent(content, queryParams.outreachType);

    // A raw page shorter than the requested size is the definitive signal
    // that there's no next page. This function only sees one page at a
    // time, though, so it can't know the true cumulative count once
    // person-code filtering has dropped dependents from earlier pages -
    // callers doing infinite-scroll pagination must use isLastPage to cap
    // their own running total at exactly what they've accumulated so far,
    // rather than trusting a totalElements number derived here from
    // page * size (which overshoots whenever prior pages filtered anything
    // out, and then never gets caught up to - infinite-scroll would keep
    // requesting pages forever past the actual end of the data).
    const isLastPage = rawContent.length < finalQueryParams.size;
    const totalElements = Math.max(
      response.data.totalElements || 0,
      mappedContent.length,
    );

    // Members with no program/opportunity (memberPrograms: []) don't count
    // toward the page's "Total Records" - reported separately so the caller
    // can subtract them from the backend's own total (which includes them),
    // accumulated across pages.
    const excludedCount = content.filter(
      (item) =>
        !Array.isArray(item.memberPrograms) || item.memberPrograms.length === 0,
    ).length;

    // Items the dependent-to-primary lookup pulled in beyond the backend's own
    // page (a search matching only a family member fetches the rest of their
    // family) aren't part of the backend's total either.
    const addedCount = content.length - rawContent.length;

    return {
      content: mappedContent,
      totalElements,
      isLastPage,
      excludedCount,
      addedCount,
      rawCount: rawContent.length,
    };
  }
  return { content: [], totalElements: 0, isLastPage: true };
};

/**
 * Searches for members by name or other identifying information
 * @async
 * @function searchMembers
 * @param {string} searchTerm - The search term to find matching members
 * @returns {Promise<string[]>} Promise resolving to an array of member names (capitalized)
 * @throws {Error} Throws error if API request fails
 * @example
 * // Search for members with names containing "john"
 * const members = await searchMembers("john");
 * // Returns: ["John Doe", "Johnny Smith", ...]
 */
export const searchMembers = async (searchTerm) => {
  const url = `/outreach/filter`;
  const params = {
    outreachType: "PRIORITY_OUTREACH",
    page: 0,
    size: 30,
    search: searchTerm,
    filterType: "MEMBER",
  };

  const response = await api.get(url, {
    params,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  if (!Array.isArray(response.data)) {
    return [];
  }

  let results = [];
  if (response.data.content.length > 0) {
    results = response.data.content
      .map((item) => {
        const name = item.member?.name || item.name || item.memberName || null;
        return name ? capitalizeFirstLetter(name) : null;
      })
      .filter(Boolean);
  }

  return results;
};

/**
 * Searches for pharmacies by name or other identifying information
 * @async
 * @function searchPharmacies
 * @param {string} searchTerm - The search term to find matching pharmacies
 * @returns {Promise<string[]>} Promise resolving to an array of unique pharmacy names (capitalized)
 * @throws {Error} Throws error if API request fails
 * @example
 * // Search for pharmacies with names containing "cvs"
 * const pharmacies = await searchPharmacies("cvs");
 * // Returns: ["Cvs Pharmacy", "Cvs Health", ...]
 */
export const searchPharmacies = async (searchTerm) => {
  const url = `/outreach/filter`;
  const params = {
    outreachType: "PRIORITY_OUTREACH",
    page: 0,
    size: 30,
    search: searchTerm,
    filterType: "PHARMACY",
  };

  const response = await api.get(url, {
    params,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  if (!Array.isArray(response.data.content)) {
    return [];
  }

  let results = [];
  if (response.data.content.length > 0) {
    results = response.data.content
      .map((item) => {
        let name = "";

        if (item.pharmacy?.name) name = item.pharmacy.name;
        else if (item.name) name = item.name;
        else if (item.pharmacyName) name = item.pharmacyName;
        else return null;

        return capitalizeFirstLetter(name);
      })
      .filter(Boolean);

    results = [...new Set(results)];
  }

  return results;
};

/**
 * Searches for healthcare programs by name or other identifying information
 * @async
 * @function searchPrograms
 * @param {string} searchTerm - The search term to find matching programs
 * @returns {Promise<string[]>} Promise resolving to an array of unique program names (capitalized)
 * @throws {Error} Throws error if API request fails
 * @example
 * // Search for programs with names containing "diabetes"
 * const programs = await searchPrograms("diabetes");
 * // Returns: ["Diabetes Care Program", "Diabetes Management", ...]
 */
export const searchPrograms = async (searchTerm) => {
  const url = `/outreach/filter`;
  const params = {
    outreachType: "PRIORITY_OUTREACH",
    page: 0,
    size: 30,
    search: searchTerm,
    filterType: "PROGRAM",
  };

  const response = await api.get(url, {
    params,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  if (!Array.isArray(response.data.content)) {
    return [];
  }

  let results = [];
  if (response.data.content.length > 0) {
    results = response.data.content
      .flatMap((item) => {
        let name = "";

        if (Array.isArray(item.programs))
          return item.programs.map((p) => capitalizeFirstLetter(p));

        if (item.program?.name) name = item.program.name;
        else if (item.name) name = item.name;
        else if (item.programName) name = item.programName;
        else return [];

        return [capitalizeFirstLetter(name)];
      })
      .filter(Boolean);

    results = [...new Set(results)];
  }

  return results;
};

/**
 * Retrieves all available filter options for the priority outreach interface
 * @async
 * @function getFilterOptions
 * @returns {Promise<Object>} Promise resolving to an object containing filter options:
 *   - members: Array of unique member names
 *   - providers: Array of unique provider names
 *   - pharmacies: Array of unique pharmacy names
 *   - programs: Array of unique program names
 * @throws {Error} Throws error if API request fails
 * @example
 * // Get all available filter options
 * const options = await getFilterOptions();
 * // Returns: {
 * //   members: ["John Doe", "Jane Smith", ...],
 * //   providers: ["City Medical", "Health Center", ...],
 * //   pharmacies: ["CVS", "Walgreens", ...],
 * //   programs: ["Diabetes Care", "Heart Health", ...]
 * // }
 */
export const getFilterOptions = async () => {
  const response = await fetchPriorityOutreach({ size: 30 });

  if (!Array.isArray(response)) {
    return {
      programs: [],
      carriers: [],
      groups: [],
      assignees: [],
    };
  }

  const programs = [
    ...new Set(response.flatMap((item) => item.programs || []).filter(Boolean)),
  ];

  const carriers = [
    ...new Set(response.flatMap((item) => item.carriers || []).filter(Boolean)),
  ];

  const groups = [
    ...new Set(response.flatMap((item) => item.groups || []).filter(Boolean)),
  ];
  const assignees = [
    ...new Set(
      response.flatMap((item) => item.assignees || []).filter(Boolean),
    ),
  ];
  return {
    programs,
    carriers,
    groups,
    assignees,
  };
};

/**
 * Updates a member's personal information
 * @async
 * @function updateMemberPersonalInfo
 * @param {string} memberId - The unique identifier of the member to update
 * @param {Object} personalInfo - Object containing the personal information to update
 * @param {string[]|string} [personalInfo.allergies] - Member's allergies (array or comma-separated string)
 * @param {string} [personalInfo.language] - Member's preferred language
 * @param {string} [personalInfo.timeZone] - Member's timezone
 * @param {string} [personalInfo.gender] - Member's gender
 * @param {string} [personalInfo.dateOfBirth] - Member's date of birth
 * @returns {Promise<Object>} Promise resolving to the updated member data
 * @throws {Error} Throws error if API request fails or member not found
 * @example
 * // Update member's language and allergies
 * const updated = await updateMemberPersonalInfo("123", {
 *   language: "Spanish",
 *   allergies: ["Penicillin", "Shellfish"],
 *   timeZone: "PST"
 * });
 */
export const updateMemberPersonalInfo = async (memberId, personalInfo) => {
  const response = await api.patch(
    `/outreach/members/${memberId}/personal-info`,
    {
      allergy: Array.isArray(personalInfo.allergies)
        ? personalInfo.allergies.join(", ")
        : personalInfo.allergies,
      language: personalInfo.language,
      timezone: personalInfo.timeZone,
      gender: personalInfo.gender,
      dob: personalInfo.dateOfBirth,

      alternatePhoneNum: personalInfo.alternatePhoneNum,
      alternateEmailAddress: personalInfo.alternateEmailAddress,
      phone: personalInfo.phone,
      email: personalInfo.email,
      preferredContactMethod: personalInfo.preferredContactMethod,
      address: personalInfo.address,
      textingConsentObtained: personalInfo.textingConsentObtained,
    },
  );
  return response.data;
};

/**
 * Re-runs the outreach-snapshot merge for this member (and their family) so
 * a change made on the member detail page shows up on the Member Outreach
 * list without waiting for the backend's scheduled cache warmup. Member
 * creation and assignee updates already trigger this automatically on the
 * backend; adding a disposition doesn't, so the Recent Activity column can
 * otherwise keep showing stale/N/A data for that member until the next
 * warmup cycle.
 * @async
 * @function resyncOutreachCache
 * @param {string} memberId - The unique identifier of the member
 * @returns {Promise<void>}
 */
export const resyncOutreachCache = async (memberId) => {
  clearMemberOverviewCache(memberId);
  await api.post(`/outreach/members/${memberId}/resync-cache`, null, {
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });
};

/**
 * Fetches comprehensive overview information for a specific member
 * @async
 * @function fetchMemberOverview
 * @param {string} memberId - The unique identifier of the member
 * @returns {Promise<Object>} Promise resolving to a comprehensive member object containing:
 *   - name: Member's full name (capitalized)
 *   - sex: Member's biological sex
 *   - gender: Member's gender identity
 *   - age: Calculated age based on date of birth
 *   - dob: Date of birth
 *   - phone: Primary phone number
 *   - preferredPhoneNum: Preferred phone number
 *   - email: Email address
 *   - timeZone: Member's timezone
 *   - memberId: Unique member identifier
 *   - personCode: Person code identifier
 *   - status: Member status (default: "Active")
 *   - language: Preferred language
 *   - address: Complete address object with street, city, state, zip, and fullAddress
 *   - carrier: Insurance carrier
 *   - group: Insurance group
 *   - subscriberNum: Insurance subscriber number
 *   - allergies: Array of known allergies
 *   - doNotCall: Do not call flag
 * @throws {Error} Throws error if API request fails or member not found
 * @example
 * // Fetch complete member overview
 * const member = await fetchMemberOverview("123");
 * // Returns: {
 * //   name: "John Doe",
 * //   age: 45,
 * //   phone: "555-1234",
 * //   address: { street: "123 Main St", city: "Anytown", ... },
 * //   allergies: ["Penicillin"],
 * //   ...
 * // }
 */
/**
 * Updates member's contact information
 * @param {string} memberId - The ID of the member
 * @param {Object} contactInfo - The contact information to update
 * @param {string} [contactInfo.alternatePhoneNumber] - Alternate phone number
 * @param {string} [contactInfo.alternateEmailAddress] - Alternate email address
 * @param {string} [contactInfo.preferredContactMethod] - Preferred contact method (PHONE/EMAIL)
 * @returns {Promise<Object>} The updated contact information
 */
export const updateMemberContactInfo = async (memberId, contactInfo) => {
  const response = await api.patch(
    `/outreach/members/${memberId}/contact`,
    {
      alternatePhoneNumber: contactInfo.alternate_phone || null,
      alternateEmailAddress: contactInfo.alternate_email || null,
      preferredContactMethod:
        contactInfo.preferredContactMethod || "Phone Number",
      textingConsentObtained: contactInfo.textingConsentObtained || false,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    },
  );
  return response.data;
};

const _fetchMemberOverview = async (memberId) => {
  const response = await api.get(`/outreach/members/${memberId}`, {
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  if (response.data) {
    const memberData = response.data;
    const phone = memberData.phoneNumber || memberData.preferredPhoneNum || "";

    return {
      name: `${capitalizeFirstLetter(memberData.fullName || "")}`.trim(),
      sex: memberData.sex || "",
      gender: memberData.gender || "",
      age: memberData.age || "",
      dob: memberData.dob || "",
      firstName: memberData.firstName || "",
      lastName: memberData.lastName || "",

      phone: phone,
      preferredPhoneNum: memberData.preferredPhoneNum || "",
      preferredPhoneNumber: memberData.preferredPhoneNumber || "",

      contact: phone,
      email: memberData.email || "",
      timeZone: memberData.timezone || "",
      memberId: memberData.id ? memberData.id.toString() : "",
      id: memberData.id ? memberData.id.toString() : "",
      personCode: memberData.personCode || "",
      status: "Active",
      dispositionStatus: capitalizeFirstLetter(memberData.status) || "",
      language: memberData.language || "",

      address:
        memberData.address && typeof memberData.address === "object"
          ? {
              line1: memberData.address.line1 || "",
              line2: memberData.address.line2 || "",
              city: memberData.address.city || "",
              state: memberData.address.state || "",
              postalCode: memberData.address.postalCode || "",
              fullAddress: [
                memberData.address.line1,
                memberData.address.line2,
                [
                  memberData.address.city,
                  memberData.address.state,
                  memberData.address.postalCode,
                ]
                  .filter(Boolean)
                  .join(" "),
              ]
                .filter(Boolean)
                .join(", "),
            }
          : {
              line1: memberData.address || "",
              line2: "",
              city: memberData.city || "",
              state: memberData.state || "",
              postalCode: memberData.postalCode || memberData.zip || "",
              fullAddress: [
                memberData.address,
                [
                  memberData.city,
                  memberData.state,
                  memberData.postalCode || memberData.zip,
                ]
                  .filter(Boolean)
                  .join(" "),
              ]
                .filter(Boolean)
                .join(", "),
            },

      carrierName: memberData.carrierName || "",
      carrierCode: memberData.carrierCode || "",
      groupName: memberData.groupName || "",
      groupNum: memberData.groupNum || "",
      subscriberNum: memberData.subscriberNum || "",
      allergies: memberData.allergy ? [memberData.allergy] : [],

      notes: [],
      dispositions: [],
      doNotCall: memberData.doNotCall || false,
      pcaDirective: memberData.pcaDirective || "",
      pcaDirectiveEnabled: memberData.pcaDirectiveEnabled ?? false,

      alternatePhoneNum: memberData.alternatePhoneNum || "",
      alternatePhoneNumber: memberData.alternatePhoneNumber || "",
      alternateEmailAddress: memberData.alternateEmailAddress || "",
      alternateEmail: memberData.alternateEmail || "",
      preferredContactMethod: memberData.preferredContactMethod || "PHONE",
      textingConsentObtained: memberData.textingConsentObtained || false,
      textingConsentUpdatedAt: memberData.textingConsentUpdatedAt || "",
      textingConsentUpdatedBy: memberData.textingConsentUpdatedBy || "",
      assignee: memberData.assignee || "",
      assigneeEmail: memberData.assigneeEmail || "",
      priorityLevel: memberData.priorityLevel || "",
      copayBin: memberData.copayBin || memberData.bin || "",
      copayPcn: memberData.copayPcn || memberData.pcn || "",
      copayGrpId: memberData.copayGrpId || memberData.grpId || "",
      copayMemberId:
        memberData.copayMemberId || memberData.copayCardMemberId || "",
      copayExpirationDate:
        memberData.copayExpirationDate || memberData.expirationDate || "",
      copayExecutionStatus:
        memberData.copayExecutionStatus ||
        memberData.executionStatus ||
        memberData.copayStatus ||
        "",
      relCode: memberData.relCode || "",
      careGuideEffectiveDate: memberData.careGuideEffectiveDate || "",
      groupStartDate: memberData.groupStartDate || "",
      groupEndDate: memberData.groupEndDate || "",
      scriptcoPatientGuid: memberData.scriptcoPatientGuid || "",
      assignedPcaId: memberData.assignedPcaId ?? null,
      assignedPca: memberData.assignedPca || "",
      assignedPcaEmail: memberData.assignedPcaEmail || "",
      assignedPcaUpdatedAt: memberData.assignedPcaUpdatedAt || "",
      assignedRnId: memberData.assignedRnId ?? null,
      assignedRn: memberData.assignedRn || "",
      assignedRnEmail: memberData.assignedRnEmail || "",
      assignedRnUpdatedAt: memberData.assignedRnUpdatedAt || "",
    };
  }
};

// A member with no outreach program of their own has no real numeric member
// id yet - the backend gives them a synthetic "{subscriberNum}-{personCode}"
// id instead (e.g. "011800034000-01"), which the single-member endpoint
// can't resolve ("Member not found"). A real member id is always plain
// numeric, so any hyphen is the signal that we're dealing with one of these.
const isSyntheticMemberId = (memberId) => String(memberId).includes("-");

// Maps a raw /outreach search item's `member` object into the same field
// shape _fetchMemberOverview returns, for the fields that record actually
// carries. It's missing the richer, backend-computed fields (copay card,
// disposition, texting consent history, etc.) that only the single-member
// endpoint provides - those get backfilled from a sibling's overview in
// fetchMemberOverviewBySyntheticId below.
const mapRawMemberToOverviewFields = (rawMember) => {
  const phone = rawMember?.phoneNumber || "";
  const address = rawMember?.address || {};
  return {
    name: `${capitalizeFirstLetter(rawMember?.fullName || "")}`.trim(),
    sex: rawMember?.sex || "",
    age: rawMember?.age || "",
    dob: rawMember?.dob || "",
    firstName: rawMember?.firstName || "",
    lastName: rawMember?.lastName || "",
    phone,
    contact: phone,
    email: rawMember?.email || "",
    timeZone: rawMember?.timezone || "",
    personCode: rawMember?.personCode || "",
    dispositionStatus: capitalizeFirstLetter(rawMember?.status) || "",
    language: rawMember?.language || "",
    address: {
      line1: address.line1 || "",
      line2: address.line2 || "",
      city: address.city || "",
      state: address.state || "",
      postalCode: address.postalCode || "",
      fullAddress: [
        address.line1,
        address.line2,
        [address.city, address.state, address.postalCode]
          .filter(Boolean)
          .join(" "),
      ]
        .filter(Boolean)
        .join(", "),
    },
    carrierName: rawMember?.carrierName || "",
    carrierCode: rawMember?.carrierCode || "",
    groupName: rawMember?.groupName || "",
    groupNum: rawMember?.groupNum || "",
    subscriberNum: rawMember?.subscriberNum || "",
    allergies: rawMember?.allergy ? [rawMember.allergy] : [],
    doNotCall: rawMember?.doNotCall || false,
    pcaDirective: rawMember?.pcaDirective || "",
    pcaDirectiveEnabled: rawMember?.pcaDirectiveEnabled ?? false,
    alternatePhoneNum: rawMember?.alternatePhoneNum || "",
    alternateEmailAddress: rawMember?.alternateEmailAddress || "",
    preferredContactMethod: rawMember?.preferredContactMethod || "PHONE",
    textingConsentObtained: rawMember?.textingConsentObtained || false,
    assignee: rawMember?.assignee || "",
    assigneeEmail: rawMember?.assigneeEmail || "",
    relCode: rawMember?.relCode || "",
    priorityLevel: rawMember?.priorityLevel || "",
    assignedPcaId: rawMember?.assignedPcaId ?? null,
    assignedPca: rawMember?.assignedPca || "",
    assignedPcaEmail: rawMember?.assignedPcaEmail || "",
    assignedPcaUpdatedAt: rawMember?.assignedPcaUpdatedAt || "",
    assignedRnId: rawMember?.assignedRnId ?? null,
    assignedRn: rawMember?.assignedRn || "",
    assignedRnEmail: rawMember?.assignedRnEmail || "",
    assignedRnUpdatedAt: rawMember?.assignedRnUpdatedAt || "",
  };
};

// Everyone under one subscriberNum as the outreach list knows them - the
// source for the member profile's Family Members section, which needs each
// person's relCode (relationship to the cardholder). A member with no
// program of their own comes back with a synthetic "{subscriberNum}-{code}"
// id, which isn't a real member id (memberId stays null for those).
export const fetchFamilyBySubscriber = async (subscriberNum) => {
  if (!subscriberNum) return [];
  const response = await api.post(`/outreach`, buildRequestBody({}, true), {
    params: buildQueryParams(
      {
        outreachType: "MEMBER_OUTREACH",
        search: subscriberNum,
        page: 0,
        size: 200,
      },
      true,
      false,
    ),
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });
  const items = Array.isArray(response.data?.content)
    ? response.data.content
    : [];
  return items
    .filter((item) => item.member?.subscriberNum === subscriberNum)
    .map((item) => ({
      subscriberNum,
      personCode: item.member?.personCode ?? "",
      relCode: item.member?.relCode ?? null,
      firstName: capitalizeFirstLetter(item.member?.firstName || ""),
      lastName: capitalizeFirstLetter(item.member?.lastName || ""),
      memberId:
        item.member?.id != null && !isSyntheticMemberId(item.member.id)
          ? item.member.id
          : null,
    }));
};

// Resolves a synthetic id by searching /outreach for its subscriberNum and
// matching the record whose person code is the target one - that record's
// own fields (name, dob, address, etc.) populate the page. Any sibling in
// that same family who does have a real member id gets their overview
// fetched too, to backfill the account-level fields (copay card,
// disposition, etc.) the target's programless record doesn't carry;
// self's fields always take precedence over the borrowed sibling data.
const fetchMemberOverviewBySyntheticId = async (syntheticId) => {
  const separatorIndex = syntheticId.lastIndexOf("-");
  const subscriberNum = syntheticId.slice(0, separatorIndex);
  const personCode = syntheticId.slice(separatorIndex + 1);

  const response = await api.post(`/outreach`, buildRequestBody({}, true), {
    params: buildQueryParams(
      {
        outreachType: "MEMBER_OUTREACH",
        search: subscriberNum,
        page: 0,
        size: 200,
      },
      true,
      false,
    ),
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });
  const familyRecords = Array.isArray(response.data?.content)
    ? response.data.content
    : [];

  const selfRecord = familyRecords.find(
    (item) => item.member?.personCode === personCode,
  );
  const sibling = familyRecords.find(
    (item) =>
      item.member?.personCode !== personCode &&
      item.member?.id != null &&
      !isSyntheticMemberId(item.member.id),
  );

  const siblingOverview = sibling
    ? await fetchMemberOverview(String(sibling.member.id)).catch((e) => {
        console.error("Failed to load sibling overview", e);
        return null;
      })
    : null;

  const selfFields = mapRawMemberToOverviewFields(selfRecord?.member);

  return {
    ...siblingOverview,
    ...selfFields,
    // PCA/RN assignment is a family-wide concept, not per-person - a
    // programless primary's own raw record usually has no assignment of its
    // own (""/null), and that emptiness must not blank out a real one the
    // sibling's fuller overview already resolved. Self still wins whenever
    // it does carry its own value.
    assignedPcaId: selfFields.assignedPcaId ?? siblingOverview?.assignedPcaId,
    assignedPca: selfFields.assignedPca || siblingOverview?.assignedPca || "",
    assignedPcaEmail:
      selfFields.assignedPcaEmail || siblingOverview?.assignedPcaEmail || "",
    assignedPcaUpdatedAt:
      selfFields.assignedPcaUpdatedAt ||
      siblingOverview?.assignedPcaUpdatedAt ||
      "",
    assignedRnId: selfFields.assignedRnId ?? siblingOverview?.assignedRnId,
    assignedRn: selfFields.assignedRn || siblingOverview?.assignedRn || "",
    assignedRnEmail:
      selfFields.assignedRnEmail || siblingOverview?.assignedRnEmail || "",
    assignedRnUpdatedAt:
      selfFields.assignedRnUpdatedAt ||
      siblingOverview?.assignedRnUpdatedAt ||
      "",
    memberId: syntheticId,
  };
};

const inflightOverviewRequests = new Map();
const memberOverviewCache = new Map();
const OVERVIEW_CACHE_TTL = 15000; // 15 seconds

export const fetchMemberOverview = async (memberId, forceRefresh = false) => {
  if (!memberId) return null;
  const cacheKey = String(memberId);

  if (!forceRefresh && memberOverviewCache.has(cacheKey)) {
    const entry = memberOverviewCache.get(cacheKey);
    if (Date.now() - entry.timestamp < OVERVIEW_CACHE_TTL) {
      return entry.data;
    }
    memberOverviewCache.delete(cacheKey);
  }

  if (inflightOverviewRequests.has(cacheKey)) {
    return inflightOverviewRequests.get(cacheKey);
  }

  const promise = (
    isSyntheticMemberId(memberId)
      ? fetchMemberOverviewBySyntheticId(memberId)
      : _fetchMemberOverview(memberId)
  )
    .then((data) => {
      memberOverviewCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
      return data;
    })
    .finally(() => {
      inflightOverviewRequests.delete(cacheKey);
    });

  inflightOverviewRequests.set(cacheKey, promise);
  return promise;
};

export const clearMemberOverviewCache = (memberId) => {
  if (memberId) {
    memberOverviewCache.delete(String(memberId));
    inflightOverviewRequests.delete(String(memberId));
  } else {
    memberOverviewCache.clear();
    inflightOverviewRequests.clear();
  }
};

export { findPrimaryItems, relCodeKind, transformContent };
